import React, { useEffect, useRef, useState } from 'react';
import { loginWithGoogle, type AuthSuccessResponse } from '../../libs/api';
import Cookies from 'js-cookie';
import { normalizeAccessToken } from '@/utils/authToken';

interface GoogleSignInProps {
  onSuccess?: (response: AuthSuccessResponse) => void;
  onError?: (error: unknown) => void;
  className?: string;
}

interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
  clientId?: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string | undefined;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (
    element: HTMLElement | null,
    options: {
      type?: 'standard' | 'icon';
      shape?: 'rectangular' | 'pill' | 'circle' | 'square';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      text?: string;
      size?: 'large' | 'medium' | 'small';
      width?: number;
      locale?: string;
    },
  ) => void;
}

interface GoogleSDK {
  accounts?: {
    id?: GoogleAccountsId;
  };
}

declare global {
  interface Window {
    google: GoogleSDK;
  }
}

const GoogleSignIn: React.FC<GoogleSignInProps> = ({
  onSuccess,
  onError,
  className = 'google-signin-btn',
}) => {
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const scriptLoaded = useRef(false);
  const scriptElementRef = useRef<HTMLScriptElement | null>(null);
  const buttonInitialized = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const retryCountRef = useRef(0);
  const initRetryCountRef = useRef(0);
  const MAX_RETRY_COUNT = 3;
  const MAX_INIT_RETRY_COUNT = 10;

  const handleCredentialResponse = async (response: GoogleCredentialResponse) => {
    try {
      console.log('Google ID Token:', response.credential);

      // 调用后端API验证Google token并登录
      const result = await loginWithGoogle({
        idToken: response.credential,
      });

      console.log('登录成功:', result);

      // 设置认证数据
      const accessToken = normalizeAccessToken(result.accessToken);
      if (accessToken) {
        Cookies.set('accessToken', accessToken, { expires: 7 });
      }
      Cookies.set('userId', result.userId, { expires: 7 });
      localStorage.setItem('userInfo', JSON.stringify(result));

      // 获取重定向路径
      const redirectPath = sessionStorage.getItem('redirectPath') || '/';
      sessionStorage.removeItem('redirectPath');

      if (onSuccess) {
        onSuccess(result);
      } else {
        window.location.href = redirectPath;
      }
    } catch (error) {
      console.error('Google登录失败:', error);
      if (onError) {
        onError(error);
      }
    }
  };

  const loadGoogleScript = () => {
    if (scriptLoaded.current) return;

    // 检查是否已有 Google SDK
    if (window.google?.accounts?.id) {
      scriptLoaded.current = true;
      initializeGoogleSignIn();
      return;
    }

    // 检查是否已存在相同的 script 标签
    const existingScript = document.querySelector(
      'script[src="https://accounts.google.com/gsi/client"]',
    );
    if (existingScript) {
      scriptElementRef.current = existingScript as HTMLScriptElement;
      scriptLoaded.current = true;
      initializeGoogleSignIn();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    scriptElementRef.current = script;

    script.onload = () => {
      scriptLoaded.current = true;
      setLoadError(false);
      // 增加延迟，确保 DOM 完全渲染和 SDK 完全加载
      retryTimeoutRef.current = setTimeout(() => {
        initializeGoogleSignIn();
      }, 300);
    };

    script.onerror = () => {
      console.error('Google SDK 加载失败');
      retryCountRef.current += 1;

      // 清理失败的 script
      if (scriptElementRef.current && scriptElementRef.current.parentNode) {
        scriptElementRef.current.parentNode.removeChild(scriptElementRef.current);
        scriptElementRef.current = null;
      }

      if (retryCountRef.current >= MAX_RETRY_COUNT) {
        console.error(`Google SDK 加载失败，已达到最大重试次数 (${MAX_RETRY_COUNT})`);
        setLoadError(true);
        setIsLoading(false);
        return;
      }

      setLoadError(true);
      setIsLoading(false);
      // 10秒后重试
      retryTimeoutRef.current = setTimeout(() => {
        console.log(`正在重试加载 Google SDK (${retryCountRef.current}/${MAX_RETRY_COUNT})`);
        setLoadError(false);
        setIsLoading(true);
        loadGoogleScript();
      }, 10000);
    };

    document.head.appendChild(script);
  };

  const initializeGoogleSignIn = () => {
    // 防止重复初始化
    if (buttonInitialized.current) {
      console.log('Google 按钮已初始化，跳过');
      setIsLoading(false);
      return;
    }

    if (!window.google?.accounts?.id) {
      if (initRetryCountRef.current < MAX_INIT_RETRY_COUNT) {
        initRetryCountRef.current += 1;
        if (initRetryCountRef.current <= 2) {
          console.warn('Google SDK 尚未加载，稍后重试');
        }
        retryTimeoutRef.current = setTimeout(() => {
          initializeGoogleSignIn();
        }, 500);
      } else {
        console.error('Google SDK 初始化超时');
        setLoadError(true);
        setIsLoading(false);
      }
      return;
    }

    if (!googleButtonRef.current) {
      if (initRetryCountRef.current < MAX_INIT_RETRY_COUNT) {
        initRetryCountRef.current += 1;
        if (initRetryCountRef.current <= 2) {
          console.warn('按钮容器尚未挂载，稍后重试');
        }
        retryTimeoutRef.current = setTimeout(() => {
          initializeGoogleSignIn();
        }, 200);
      } else {
        console.error('按钮容器挂载超时');
        setLoadError(true);
        setIsLoading(false);
      }
      return;
    }

    // 检查容器是否已经包含 Google 按钮（Google SDK 会创建 iframe）
    const hasGoogleButton = googleButtonRef.current.querySelector(
      'iframe[src*="accounts.google.com"]',
    );
    if (hasGoogleButton) {
      console.log('容器已包含 Google 按钮，跳过初始化');
      buttonInitialized.current = true;
      setIsLoading(false);
      setLoadError(false);
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // 获取容器宽度
      const containerWidth = googleButtonRef.current.offsetWidth || 358;

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        shape: 'rectangular',
        theme: 'outline',
        text: 'signin_with',
        size: 'large',
        width: containerWidth,
        locale: 'zh_CN',
      });

      buttonInitialized.current = true;
      setIsLoading(false);
      setLoadError(false);
      initRetryCountRef.current = 0;
    } catch (error) {
      console.error('Google 登录按钮初始化失败:', error);
      setLoadError(true);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGoogleScript();

    return () => {
      // 清理定时器
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      // 不移除 script 标签，因为它可能被多个组件共享
      // Google SDK 可以在整个应用中复用
    };
  }, []);

  return (
    <div className={className}>
      {isLoading && (
        <div
          style={{
            textAlign: 'center',
            padding: '10px',
            color: '#999',
            fontSize: '14px',
            minHeight: '40px',
          }}
        >
          正在加载 Google 登录...
        </div>
      )}
      {loadError && (
        <div
          style={{
            textAlign: 'center',
            padding: '10px',
            color: '#f5222d',
            fontSize: '14px',
            minHeight: '40px',
          }}
        >
          {retryCountRef.current >= MAX_RETRY_COUNT
            ? 'Google 登录服务暂时不可用，请稍后再试'
            : 'Google 登录加载失败，正在重试...'}
        </div>
      )}
      <div
        ref={googleButtonRef}
        style={{
          width: '100%',
          minHeight: '40px',
          display: isLoading || loadError ? 'none' : 'block',
        }}
      />
    </div>
  );
};

export default GoogleSignIn;
